import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { updateProfile } from "@/lib/profile";
import { Check, X } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { trackOnboardingEvent } from "@/lib/onboardingAnalytics";
import { detectNativeApp, isIOS, openInExternalBrowser } from "@/lib/platform";
import { IOSAppPromotionDialog } from "./IOSAppPromotionDialog";
import { useTheme } from "@/providers/ThemeProvider";
import { cn } from "@/lib/utils";

export const UsernameStep = ({ 
  userId, 
  onComplete 
}: { 
  userId: string; 
  onComplete: () => void;
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();
  // Skip theme selection if user already chose during metrics onboarding
  const themeAlreadyChosen = localStorage.getItem('gitfit-theme-chosen') === 'true';
  const [step, setStep] = useState<'theme' | 'username'>(themeAlreadyChosen ? 'username' : 'theme');
  const [showThemeNext, setShowThemeNext] = useState(false);
  const [username, setUsername] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showIOSAppDialog, setShowIOSAppDialog] = useState(false);

  useEffect(() => {
    if (!username) {
      setIsAvailable(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      checkUsername();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [username]);

  useEffect(() => {
    if (step === 'theme') {
      const timer = setTimeout(() => setShowThemeNext(true), 100);
      return () => clearTimeout(timer);
    }
  }, [step]);

  const checkUsername = async () => {
    if (!username || username.length < 3) {
      setIsAvailable(null);
      return;
    }

    setIsChecking(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("username")
        .eq("username", username.toLowerCase())
        .maybeSingle();

      if (error) throw error;
      setIsAvailable(!data);
    } catch (error) {
      console.error("Error checking username:", error);
      setIsAvailable(null);
    } finally {
      setIsChecking(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("[UsernameStep] Form submitted");

    if (!username || username.length < 3) {
      toast({
        title: "Invalid username",
        description: "Username must be at least 3 characters long",
        variant: "destructive",
      });
      return;
    }

    if (!isAvailable) {
      toast({
        title: "Username unavailable",
        description: "Please choose a different username",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Use updateProfile with read-back guarantee
      const fresh = await updateProfile(supabase, userId, {
        username: username.toLowerCase()
      });

      if (!fresh?.username) {
        throw new Error('Username not persisted yet - please try again');
      }

      console.log("[UsernameStep] Username set successfully, verified:", fresh.username);

      // Track username creation
      trackOnboardingEvent({ eventType: "username_created" });

      // Update the cache with fresh data
      queryClient.setQueryData(["profile", userId], fresh);
      queryClient.invalidateQueries({ queryKey: ["profile", userId] });

      console.log("[UsernameStep] Username setup complete, checking platform");
      
      // Check if we're on web (not native app)
      const platformDetection = await detectNativeApp();
      const isWebVersion = !platformDetection.isNative;
      
      if (isWebVersion) {
        console.log("[UsernameStep] Web version detected - showing app promotion dialog");
        setShowIOSAppDialog(true);
        // Don't call onComplete() yet - wait for user to dismiss the dialog
      } else {
        console.log("[UsernameStep] Native app detected - calling onComplete directly");
        // CRITICAL: This MUST start the tutorial sequence
        // The onComplete callback should advance to the tutorial step
        onComplete();
      }
    } catch (error: any) {
      console.error("[UsernameStep] Error setting username:", error);
      
      // Check if it's a unique constraint violation (deleted account with same username)
      const errorMessage = error.message || String(error);
      if (errorMessage.includes('duplicate key') || errorMessage.includes('profiles_username_key')) {
        toast({
          title: "Username unavailable",
          description: "This username is already taken. Please choose a different one.",
          variant: "destructive",
        });
        // Re-check availability
        setIsAvailable(false);
      } else {
        toast({
          title: "Error",
          description: error.message || "Failed to set username. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const sanitizeUsername = (value: string) => {
    return value.toLowerCase().replace(/[^a-z0-9_]/g, "");
  };

  const handleContinueOnWeb = () => {
    console.log("[UsernameStep] User chose to continue on web");
    setShowIOSAppDialog(false);
    onComplete();
  };

  if (step === 'theme') {
    return (
      <div className="w-full max-w-md space-y-8 animate-in fade-in duration-500">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-bold">{t('ui.text.pick_your_theme')}</h2>
        </div>

        <div className="grid grid-cols-3 gap-4 px-2">
          <div 
            className="space-y-2 cursor-pointer group"
            onClick={() => setTheme('light')}
          >
            <div className={cn(
              "relative rounded-[2rem] overflow-hidden border-4 transition-all duration-300",
              theme === 'light' 
                ? "border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.3)] scale-105" 
                : "border-transparent hover:scale-105 opacity-70 hover:opacity-100"
            )}>
              <img 
                src="/iphone-screenshots/light-mode.png" 
                alt={t('accessibility.labels.light_mode')}
                className="w-full h-auto object-cover"
              />
            </div>
            <p className={cn(
              "text-center text-sm font-medium transition-colors",
              theme === 'light' ? "text-foreground" : "text-muted-foreground"
            )}>{t('common.light')}</p>
          </div>

          <div 
            className="space-y-2 cursor-pointer group"
            onClick={() => setTheme('system')}
          >
            <div className={cn(
              "relative rounded-[2rem] overflow-hidden border-4 transition-all duration-300",
              theme === 'system' 
                ? "border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.3)] scale-105" 
                : "border-transparent hover:scale-105 opacity-70 hover:opacity-100"
            )}>
              <img 
                src="/iphone-screenshots/system-mode.png" 
                alt={t('accessibility.labels.system_mode')}
                className="w-full h-auto object-cover"
              />
            </div>
            <p className={cn(
              "text-center text-sm font-medium transition-colors",
              theme === 'system' ? "text-foreground" : "text-muted-foreground"
            )}>{t('common.system')}</p>
          </div>

          <div 
            className="space-y-2 cursor-pointer group"
            onClick={() => setTheme('dark')}
          >
            <div className={cn(
              "relative rounded-[2rem] overflow-hidden border-4 transition-all duration-300",
              theme === 'dark' 
                ? "border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.3)] scale-105" 
                : "border-transparent hover:scale-105 opacity-70 hover:opacity-100"
            )}>
              <img 
                src="/iphone-screenshots/dark-mode.png" 
                alt={t('accessibility.labels.dark_mode')}
                className="w-full h-auto object-cover"
              />
            </div>
            <p className={cn(
              "text-center text-sm font-medium transition-colors",
              theme === 'dark' ? "text-foreground" : "text-muted-foreground"
            )}>{t('common.dark')}</p>
          </div>
        </div>

        <div className={cn(
          "flex justify-center transition-all duration-700 ease-out transform",
          showThemeNext ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-0"
        )}>
          <Button
            size="lg"
            className="w-full max-w-[200px]"
            onClick={() => setStep('username')}
          >
            {t('common.next')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <IOSAppPromotionDialog 
        open={showIOSAppDialog}
        onContinueOnWeb={handleContinueOnWeb}
      />
      
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-bold">{t('ui.text.choose_your_username')}</h2>
          <p className="text-muted-foreground">{t('ui.text.this_is_how_others_will_see_you')}</p>
        </div>

      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <div className="relative">
              <Input
                placeholder="username"
                value={username}
                onChange={(e) => setUsername(sanitizeUsername(e.target.value))}
                className="pr-10"
                autoFocus
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {isChecking && <MiniGridLoader className="w-4 h-4 text-muted-foreground" />}
                {!isChecking && isAvailable === true && (
                  <Check className="w-4 h-4 text-green-600" />
                )}
                {!isChecking && isAvailable === false && (
                  <X className="w-4 h-4 text-destructive" />
                )}
              </div>
            </div>

            {username && username.length < 3 && (
              <p className="text-xs text-muted-foreground">{t('ui.text.username_must_be_at_least_3_characters')}</p>
            )}
            {isAvailable === false && (
              <p className="text-xs text-destructive">{t('ui.text.this_username_is_taken')}</p>
            )}
            {isAvailable === true && (
              <p className="text-xs text-green-600">{t('ui.text.this_username_is_available')}</p>
            )}
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={!isAvailable || isSubmitting || isChecking}
            onClick={(e) => {
              console.log("[UsernameStep] Continue button clicked - will trigger tutorial");
            }}
          >
            {isSubmitting ? "Creating profile..." : "Continue"}
          </Button>
        </form>
      </Card>
      </div>
    </>
  );
};
